import { apiClient } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';
import { tokenStore } from '@/lib/api/token-store';

import { userSchema } from './schemas';
import type { AuthSuccess, LoginPasswordRequest, OtpDispatch, SendOtpRequest, User } from './types';

function adoptSession(payload: { accessToken: string; user: unknown }): AuthSuccess {
  const { accessToken, user } = payload;
  const parsed = userSchema.parse(user);
  tokenStore.setAccessToken(accessToken);
  return { accessToken, user: parsed };
}

export async function loginWithPassword(body: LoginPasswordRequest): Promise<AuthSuccess> {
  return adoptSession(
    await apiClient.post<{ accessToken: string; user: unknown }>(endpoints.auth.login, body),
  );
}

export async function sendLoginOtp(body: SendOtpRequest): Promise<OtpDispatch> {
  return apiClient.post<OtpDispatch>(endpoints.auth.otpSend, body);
}

export async function verifyLoginOtp(body: {
  referenceId: string;
  otp: string;
}): Promise<AuthSuccess> {
  return adoptSession(
    await apiClient.post<{ accessToken: string; user: unknown }>(endpoints.auth.otpVerify, body),
  );
}

export async function sendForgotOtp(phone: string): Promise<OtpDispatch> {
  return apiClient.post<OtpDispatch>(endpoints.auth.forgotSend, { phone });
}

export async function verifyForgotOtp(body: { referenceId: string; otp: string }): Promise<string> {
  const { passwordResetToken } = await apiClient.post<{ passwordResetToken: string }>(
    endpoints.auth.forgotVerify,
    body,
  );
  tokenStore.setResetToken(passwordResetToken);
  return passwordResetToken;
}

export async function resetPassword(newPassword: string): Promise<void> {
  const token = tokenStore.getResetToken();
  await apiClient.post(
    endpoints.auth.forgotReset,
    { newPassword },
    { headers: token ? { Authorization: `Bearer ${token}` } : undefined },
  );
}

export async function fetchCurrentUser(): Promise<User> {
  return userSchema.parse(await apiClient.get(endpoints.auth.me));
}

export async function logout(): Promise<void> {
  await apiClient.post(endpoints.auth.logout);
  tokenStore.clear();
}

export async function refreshSession(): Promise<User> {
  await apiClient.post(endpoints.auth.refresh);
  return fetchCurrentUser();
}
